/**
 * shared/activity-text.ts - what of an agent's live activity the owner may see. Pure functions: no I/O.
 *
 * The agent runtimes report everything through onThought: the runtime's own nudges to the model ("Completion check: STOP - you
 * have only created 1 post(s)... Call generate_post"), tool calls with their raw JSON ("→ schedule_post({...})") and the model's
 * text, which sometimes carries a tool call written out. 2026-11-01 the Draper page showed all of it while working.
 * Internal nudges are hidden; a tool call shows as a plain label; raw call JSON is cut out of any text.
 */

/** Runtime-to-model instructions: never shown. */
const INTERNAL_RE = /^(?:\[Step \d+\/\d+\]\s*)?(?:Completion check:|Model wrote a tool call as text|STOP\b|Do NOT write tool calls)/i;
/** A nudge that slipped into a longer line ("... Call generate_post immediately ..."). */
const NUDGE_RE = /\b(?:you are NOT done|Call [a-z]+_[a-z_]+ (?:immediately|now|again))\b/i;
/** "→ schedule_post({...})" as the runtimes emit a tool call. */
const ACTION_RE = /^→\s*([a-z][a-z0-9]*(?:_[a-z0-9]+)+)\s*\(/i;
/** A written-out call with JSON args: schedule_post({...}), tool_code blocks, print(fn(...)). */
const RAW_CALL_RE = /```(?:tool_code|json)?[\s\S]*?```|\bprint\(\s*[a-z_]+\([\s\S]*?\)\s*\)|\b[a-z][a-z0-9]*(?:_[a-z0-9]+)+\s*\(\s*\{[\s\S]*?\}\s*\)|\b[a-z][a-z0-9]*(?:_[a-z0-9]+)+\s*\(\s*\{[\s\S]*$/gi;

/** "schedule_post" -> "Schedule post". */
export function toolLabel(name: string): string {
  const s = String(name || "").replace(/_/g, " ").trim();
  return s ? s[0].toUpperCase() + s.slice(1) : s;
}

/** The text to show for one activity line, or null to hide it. */
export function publicActivityText(action: string | null | undefined): string | null {
  const a = String(action ?? "").trim();
  if (!a) return null;
  if (INTERNAL_RE.test(a) || NUDGE_RE.test(a)) return null;
  const call = a.match(ACTION_RE);
  if (call) return `→ ${toolLabel(call[1])}`;
  const cleaned = a.replace(RAW_CALL_RE, "").replace(/[ \t]{2,}/g, " ").trim();
  if (!cleaned || /^\[Step \d+\/\d+\]$/.test(cleaned)) return null;
  return cleaned;
}

/**
 * The same line as a plain sentence for the owner's feed and the home "Today" strip: no "[Step 2/12]" counter, no markdown
 * asterisks or backticks, a tool label reads "Working on: schedule post". null when publicActivityText hides it.
 */
export function friendlyActivityText(action: string | null | undefined): string | null {
  const shown = publicActivityText(action);
  if (shown === null) return null;
  if (shown.startsWith("→ ")) return `Working on: ${shown.slice(2).toLowerCase()}`;
  const plain = shown
    .replace(/^\[Step \d+\/\d+\]\s*/, "")
    .replace(/\*\*|__|`/g, "")
    .replace(/(^|\s)\*(\S)/g, "$1$2")
    .replace(/(\S)\*(?=\s|$)/g, "$1")
    .replace(/\\(['"])/g, "$1")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^(\w+) complete after \d+ steps?\.?$/i, "$1 finished a piece of work");
  return plain || null;
}
